/***************************************************************************

    resourceArticles.ts
    -------------------------
    last Update          : October 23, 2026
    copyright            : (C) 2025 by CIL4Sys Engineering

***************************************************************************/

import { PageSeoLang } from "./pageSeo"

export interface ResourceFaq {
	q: string
	a: string
}

export interface ResourceSection {
	heading: string
	paragraphs: Array<string>
	bullets?: Array<string>
	table?: { headers: Array<string>; rows: Array<Array<string>> }
}

export interface ResourceArticle {
	slug: string
	title: Record<PageSeoLang, string>
	description: Record<PageSeoLang, string>
	h1: Record<PageSeoLang, string>
	definition: Record<PageSeoLang, string>
	sections: Record<PageSeoLang, Array<ResourceSection>>
	faqs: Record<PageSeoLang, Array<ResourceFaq>>
	datePublished: string
	dateModified: string
}

export const RESOURCE_ARTICLES: Array<ResourceArticle> = [
	{
		slug: "customer-in-the-loop",
		datePublished: "2026-10-23",
		dateModified: "2026-10-23",
		title: {
			en: "What Is Customer In the Loop? MBSE Method Explained | CIL4Sys",
			fr: "Customer In the Loop : méthode MBSE expliquée | CIL4Sys",
		},
		description: {
			en: "Customer In the Loop is CIL4Sys’s MBSE method: stakeholders continuously validate system behavior in a shared functional digital mock-up. Definition, benefits, and how Sim4Sys supports it.",
			fr: "Customer In the Loop est la méthode MBSE de CIL4Sys : les parties prenantes valident en continu le comportement système dans une maquette numérique fonctionnelle partagée. Définition, bénéfices, Sim4Sys.",
		},
		h1: {
			en: "What is Customer In the Loop?",
			fr: "Qu’est-ce que Customer In the Loop ?",
		},
		definition: {
			en: "Customer In the Loop is a model-based systems engineering method where stakeholders continuously validate intended system behavior through a shared functional digital mock-up fed by executable models—from early concept through detailed design—before costly physical development locks decisions in.",
			fr: "Customer In the Loop est une méthode d’ingénierie système basée sur les modèles où les parties prenantes valident en continu le comportement visé du système via une maquette numérique fonctionnelle partagée, alimentée par des modèles exécutables—du concept au détail—avant que le développement physique ne fige les choix.",
		},
		sections: {
			en: [
				{
					heading: "Why document-centric V-cycles struggle",
					paragraphs: [
						"In many programs, requirements live in documents, architecture in slides, and validation waits for late integration. Misunderstandings stay hidden until hardware or software already exists. Fixes then cost schedule and rework.",
						"Customer In the Loop flips that sequence: make behavior visible and testable as soon as models exist, and invite the “customer” (internal or external stakeholder) into that loop repeatedly.",
					],
				},
				{
					heading: "How the loop works in practice",
					paragraphs: [
						"Engineers build and refine functional system models. Those models drive a functional digital mock-up where scenarios can be run, observed, and challenged by stakeholders. Agreed behavior then becomes the source for generated requirements and specifications, keeping intent and documentation aligned.",
					],
					bullets: [
						"Model operational and functional behavior early",
						"Simulate and share scenarios in a functional digital mock-up",
						"Capture stakeholder feedback while change is still cheap",
						"Generate requirements and specification documents from validated models",
					],
				},
				{
					heading: "Role of Sim4Sys",
					paragraphs: [
						"CIL4Sys implements Customer In the Loop with the Sim4Sys toolchain. Sim4Sys Designer guides modeling in environments such as Eclipse Papyrus, IBM Rhapsody, PTC Modeler, and CATIA Magic. Sim4Sys Virtual Bench runs software images in a collaborative SaaS playground for scenario-based verification and validation.",
						"Together they support a continuous engineering loop: model, simulate, validate with stakeholders, then generate consistent specifications from the trusted model.",
					],
				},
				{
					heading: "When teams use Customer In the Loop",
					paragraphs: [
						"The approach fits complex systems with many interfaces and stakeholders—defense systems of systems, industrial platforms, automotive and mobility programs—anywhere early shared understanding of behavior reduces late integration risk.",
						"It does not replace domain engineering judgment. It gives that judgment a shared, executable reference so decisions are debated against observed behavior, not ambiguous prose alone.",
					],
				},
				{
					heading: "Next steps with CIL4Sys",
					paragraphs: [
						"Explore how CIL4Sys applies the method on real programs on the use cases page, review services for model-based specification and transformation work, or see Sim4Sys and SyneSys on the solutions page. For a project discussion, use the contact form.",
					],
				},
			],
			fr: [
				{
					heading: "Pourquoi les cycles en V documentaires peinent",
					paragraphs: [
						"Dans beaucoup de programmes, les exigences vivent dans des documents, l’architecture dans des slides, et la validation attend une intégration tardive. Les malentendus restent cachés jusqu’à ce que le matériel ou le logiciel existe déjà. Les corrections coûtent alors du planning et des reprises.",
						"Customer In the Loop inverse la séquence : rendre le comportement visible et testable dès que les modèles existent, et faire entrer le « client » (partie prenante interne ou externe) dans cette boucle de façon répétée.",
					],
				},
				{
					heading: "Comment la boucle fonctionne concrètement",
					paragraphs: [
						"Les ingénieurs construisent et raffinent des modèles système fonctionnels. Ces modèles alimentent une maquette numérique fonctionnelle où des scénarios peuvent être exécutés, observés et challengés par les parties prenantes. Le comportement convenu devient ensuite la source des exigences et spécifications générées, pour aligner intention et documentation.",
					],
					bullets: [
						"Modéliser tôt le comportement opérationnel et fonctionnel",
						"Simuler et partager des scénarios dans une maquette numérique fonctionnelle",
						"Recueillir le feedback des parties prenantes tant que le changement est peu coûteux",
						"Générer exigences et documents de spécification à partir de modèles validés",
					],
				},
				{
					heading: "Le rôle de Sim4Sys",
					paragraphs: [
						"CIL4Sys met en œuvre Customer In the Loop avec la chaîne Sim4Sys. Sim4Sys Designer guide la modélisation dans des environnements tels qu’Eclipse Papyrus, IBM Rhapsody, PTC Modeler et CATIA Magic. Sim4Sys Virtual Bench exécute des images logicielles dans un playground SaaS collaboratif pour la vérification et validation par scénarios.",
						"Ensemble, ils soutiennent une boucle d’ingénierie continue : modéliser, simuler, valider avec les parties prenantes, puis générer des spécifications cohérentes depuis le modèle de confiance.",
					],
				},
				{
					heading: "Quand les équipes utilisent Customer In the Loop",
					paragraphs: [
						"L’approche convient aux systèmes complexes à nombreuses interfaces et parties prenantes—systèmes de systèmes de défense, plateformes industrielles, programmes automobile et mobilité—partout où une compréhension partagée du comportement réduit le risque d’intégration tardive.",
						"Elle ne remplace pas le jugement d’ingénierie métier. Elle donne à ce jugement une référence partagée et exécutable pour débattre des décisions sur un comportement observé, pas seulement sur une prose ambiguë.",
					],
				},
				{
					heading: "Prochaines étapes avec CIL4Sys",
					paragraphs: [
						"Découvrez comment CIL4Sys applique la méthode sur des programmes réels via les cas d’usage, les services de spécification et de transformation, ou Sim4Sys et SyneSys sur la page solutions. Pour un échange projet, utilisez le formulaire de contact.",
					],
				},
			],
		},
		faqs: {
			en: [
				{
					q: "Is Customer In the Loop the same as MBSE?",
					a: "MBSE is the broader discipline of using models as the primary engineering artifacts. Customer In the Loop is CIL4Sys’s specific method for keeping stakeholders inside a continuous validation loop using a functional digital mock-up.",
				},
				{
					q: "Do you need Sim4Sys to apply Customer In the Loop?",
					a: "The method is tool-enabled by Sim4Sys at CIL4Sys, but the principle—early shared validation of behavior—can guide any MBSE practice. Sim4Sys Designer and Virtual Bench are how CIL4Sys operationalizes it.",
				},
				{
					q: "Who is the “customer” in Customer In the Loop?",
					a: "The customer is any stakeholder who must accept system behavior: end customer, program authority, neighboring teams, or operators. The loop makes their feedback continuous rather than a late review gate.",
				},
			],
			fr: [
				{
					q: "Customer In the Loop, est-ce la même chose que le MBSE ?",
					a: "Le MBSE est la discipline large qui place les modèles comme artefacts principaux. Customer In the Loop est la méthode spécifique de CIL4Sys pour garder les parties prenantes dans une boucle de validation continue via une maquette numérique fonctionnelle.",
				},
				{
					q: "Faut-il Sim4Sys pour appliquer Customer In the Loop ?",
					a: "Chez CIL4Sys, la méthode est outillée par Sim4Sys, mais le principe—valider tôt et collectivement le comportement—peut guider toute pratique MBSE. Designer et Virtual Bench sont la mise en œuvre CIL4Sys.",
				},
				{
					q: "Qui est le « client » dans Customer In the Loop ?",
					a: "Le client désigne toute partie prenante qui doit accepter le comportement du système : client final, autorité de programme, équipes voisines ou opérateurs. La boucle rend leur feedback continu, pas seulement une revue tardive.",
				},
			],
		},
	},
	{
		slug: "sysml-v2-vs-v1",
		datePublished: "2026-10-23",
		dateModified: "2026-10-23",
		title: {
			en: "SysML v2 vs SysML v1: What Changes for MBSE Teams | CIL4Sys",
			fr: "SysML v2 vs SysML v1 : ce qui change pour le MBSE | CIL4Sys",
		},
		description: {
			en: "SysML v2 vs SysML v1 compared for MBSE teams: textual syntax, API, modeling concepts, and when to migrate. Includes how CIL4Sys SysML v2 training fits.",
			fr: "SysML v2 vs SysML v1 pour les équipes MBSE : syntaxe textuelle, API, concepts de modélisation, et quand migrer. Lien avec la formation SysML v2 CIL4Sys.",
		},
		h1: {
			en: "SysML v2 vs SysML v1: what changes for MBSE teams?",
			fr: "SysML v2 vs SysML v1 : que change pour les équipes MBSE ?",
		},
		definition: {
			en: "SysML v2 is the next-generation Systems Modeling Language standardized for MBSE. Compared with SysML v1, it adds a first-class textual syntax alongside graphics, a standard API for tool interoperability, and refined modeling concepts aimed at clearer, more executable system models.",
			fr: "SysML v2 est la nouvelle génération du Systems Modeling Language pour le MBSE. Par rapport à SysML v1, il ajoute une syntaxe textuelle de premier plan à côté du graphique, une API standard d’interopérabilité des outils, et des concepts de modélisation affinés pour des modèles système plus clairs et plus exploitables.",
		},
		sections: {
			en: [
				{
					heading: "What is SysML v2?",
					paragraphs: [
						"SysML (Systems Modeling Language) is a modeling language used in model-based systems engineering to describe requirements, structure, behavior, and related views of a system of interest. SysML v1 has been widely used through diagram-centric tools. SysML v2 revisits the language foundations so models can be expressed textually and graphically, queried through a standard API, and shared more consistently across tools and organizations.",
						"For teams, the practical question is not “is v2 better on paper?” but “what changes in daily modeling, training, and toolchain choices?”",
					],
				},
				{
					heading: "SysML v2 vs SysML v1 at a glance",
					paragraphs: [
						"The table below summarizes differences that matter for engineering managers and practitioners. It is a practical comparison, not a vendor ranking.",
					],
					table: {
						headers: ["Topic", "SysML v1", "SysML v2"],
						rows: [
							["Primary authoring", "Mostly diagram-first in tools", "Textual + graphical syntax"],
							["Interoperability", "Tool-specific exchange patterns", "Standard API as a core design goal"],
							["Learning curve", "Diagram vocabulary and tool UI", "Language concepts + text + diagrams"],
							["Automation potential", "Scripts/plugins vary by tool", "API-friendly model access and generation"],
							["Migration", "Mature installed base", "Adoption in progress; plan coexistence"],
						],
					},
				},
				{
					heading: "What stays the same",
					paragraphs: [
						"You still model systems of interest, requirements, structure, and behavior. MBSE intent—models as the source of engineering truth—does not disappear with v2. Stakeholder communication, configuration control, and V&V discipline remain essential.",
					],
				},
				{
					heading: "When to start with SysML v2",
					paragraphs: [
						"Start exploring SysML v2 when you need stronger tool interoperability, want textual modeling for review and generation workflows, or are launching a new program that can choose its language baseline. Keep SysML v1 skills current if your suppliers and legacy models still depend on it.",
						"Many organizations will run a coexistence period: v1 on sustaining programs, v2 on selected new workstreams while teams train.",
					],
					bullets: [
						"New greenfield MBSE initiative with API/automation goals",
						"Need for consistent exchange beyond a single modeling tool",
						"Training budget available before a major program ramp",
						"Legacy v1 models that must stay editable for years → plan dual competence",
					],
				},
				{
					heading: "How CIL4Sys training helps",
					paragraphs: [
						"CIL4Sys offers an introduction to SysML v2 as part of its training catalog, alongside broader systems engineering and hands-on Sim4Sys courses. The SysML v2 introduction focuses on language concepts and how they support modeling of complex systems in an agile MBSE approach.",
						"If your goal is not only language literacy but executable validation with stakeholders, pair SysML learning with Customer In the Loop practice and the Sim4Sys toolchain described on the solutions page.",
					],
				},
				{
					heading: "Next steps",
					paragraphs: [
						"Review upcoming SysML and Sim4Sys sessions on the training page, read the Customer In the Loop guide for method context, or contact CIL4Sys to discuss a corporate SysML v2 / MBSE upskilling plan.",
					],
				},
			],
			fr: [
				{
					heading: "Qu’est-ce que SysML v2 ?",
					paragraphs: [
						"SysML (Systems Modeling Language) est un langage de modélisation utilisé en ingénierie système basée sur les modèles pour décrire exigences, structure, comportement et vues associées d’un système d’intérêt. SysML v1 s’est largement diffusé via des outils centrés diagrammes. SysML v2 revisite les fondations du langage pour que les modèles puissent s’exprimer en texte et en graphique, être interrogés via une API standard, et être partagés plus consistentment entre outils et organisations.",
						"Pour les équipes, la question pratique n’est pas « v2 est-il meilleur sur le papier ? » mais « que change-t-il au quotidien en modélisation, formation et choix d’outillage ? »",
					],
				},
				{
					heading: "SysML v2 vs SysML v1 en un coup d’œil",
					paragraphs: [
						"Le tableau ci-dessous résume les écarts utiles aux managers et praticiens. Comparaison pratique, pas un classement d’éditeurs.",
					],
					table: {
						headers: ["Sujet", "SysML v1", "SysML v2"],
						rows: [
							["Authoring principal", "Surtout diagrammes dans l’outil", "Syntaxe textuelle + graphique"],
							["Interopérabilité", "Échanges souvent spécifiques outil", "API standard comme objectif central"],
							["Courbe d’apprentissage", "Vocabulaire diagrammes + UI outil", "Concepts langage + texte + diagrammes"],
							["Potentiel d’automatisation", "Scripts/plugins selon l’outil", "Accès et génération de modèles via API"],
							["Migration", "Base installée mature", "Adoption en cours ; prévoir coexistence"],
						],
					},
				},
				{
					heading: "Ce qui ne change pas",
					paragraphs: [
						"On modélise toujours des systèmes d’intérêt, des exigences, de la structure et du comportement. L’intention MBSE—le modèle comme source de vérité d’ingénierie—ne disparaît pas avec la v2. Communication parties prenantes, gestion de configuration et discipline de V&V restent essentielles.",
					],
				},
				{
					heading: "Quand démarrer avec SysML v2",
					paragraphs: [
						"Explorez SysML v2 lorsque vous visez une meilleure interopérabilité d’outils, des workflows texte pour revue et génération, ou un nouveau programme libre de choisir sa baseline langage. Conservez les compétences SysML v1 si fournisseurs et modèles hérités en dépendent encore.",
						"Beaucoup d’organisations vivront une période de coexistence : v1 sur le sustaining, v2 sur des lots nouveaux pendant que les équipes se forment.",
					],
					bullets: [
						"Initiative MBSE greenfield avec objectifs API/automatisation",
						"Besoin d’échange cohérent au-delà d’un seul outil de modélisation",
						"Budget formation disponible avant une montée en charge programme",
						"Modèles v1 à maintenir des années → dual competence à planifier",
					],
				},
				{
					heading: "Comment la formation CIL4Sys aide",
					paragraphs: [
						"CIL4Sys propose une introduction à SysML v2 dans son catalogue, aux côtés de formations d’ingénierie système et de sessions pratiques Sim4Sys. L’introduction SysML v2 couvre les concepts du langage et leur apport pour modéliser des systèmes complexes dans une démarche MBSE agile.",
						"Si l’objectif dépasse la littératie langage vers une validation exécutable avec les parties prenantes, couplez l’apprentissage SysML à la méthode Customer In the Loop et à la chaîne Sim4Sys présentée sur la page solutions.",
					],
				},
				{
					heading: "Prochaines étapes",
					paragraphs: [
						"Consultez les sessions SysML et Sim4Sys sur la page formations, lisez le guide Customer In the Loop pour le contexte méthode, ou contactez CIL4Sys pour un plan de montée en compétence SysML v2 / MBSE en entreprise.",
					],
				},
			],
		},
		faqs: {
			en: [
				{
					q: "Is SysML v2 a replacement that forces immediate migration?",
					a: "No. Many organizations will coexist with SysML v1 on legacy programs while adopting SysML v2 on selected new work. Migration should be planned against toolchain readiness and supplier constraints.",
				},
				{
					q: "What is the biggest practical difference between SysML v1 and v2?",
					a: "For most practitioners, the standout shift is first-class textual modeling plus a standard API focus, not only a new set of diagrams. That changes how models are authored, reviewed, exchanged, and automated.",
				},
				{
					q: "Does CIL4Sys train SysML v2?",
					a: "Yes. CIL4Sys lists an introduction to SysML v2 in its training catalog, and can discuss corporate sessions. See the formation page for catalog details and contact for custom needs.",
				},
			],
			fr: [
				{
					q: "SysML v2 impose-t-il une migration immédiate ?",
					a: "Non. Beaucoup d’organisations feront coexister SysML v1 sur le legacy et SysML v2 sur des lots nouveaux. La migration se planifie selon la maturité outillage et les contraintes fournisseurs.",
				},
				{
					q: "Quelle est la différence pratique majeure entre SysML v1 et v2 ?",
					a: "Pour la plupart des praticiens, le changement marquant est la modélisation textuelle de premier plan et l’accent API standard, pas seulement de nouveaux diagrammes. Cela change authoring, revue, échange et automatisation.",
				},
				{
					q: "CIL4Sys forme-t-il à SysML v2 ?",
					a: "Oui. CIL4Sys propose une introduction à SysML v2 dans son catalogue, et peut discuter de sessions entreprise. Voir la page formations et le contact pour du sur-mesure.",
				},
			],
		},
	},
	{
		slug: "sim4sys-designer",
		datePublished: "2026-10-23",
		dateModified: "2026-10-23",
		title: {
			en: "Sim4Sys Designer: Guided MBSE Modeling Add-ons | CIL4Sys",
			fr: "Sim4Sys Designer : add-ons MBSE guidés | CIL4Sys",
		},
		description: {
			en: "Sim4Sys Designer is a set of MBSE add-ons for Papyrus, IBM Rhapsody, PTC Modeler, and CATIA Magic. Guided modeling, consistency checks, and generation of requirements, specs, and executable images.",
			fr: "Sim4Sys Designer est un ensemble d’add-ons MBSE pour Papyrus, IBM Rhapsody, PTC Modeler et CATIA Magic. Modélisation guidée, cohérence, génération d’exigences, spécifications et images exécutables.",
		},
		h1: {
			en: "What is Sim4Sys Designer?",
			fr: "Qu’est-ce que Sim4Sys Designer ?",
		},
		definition: {
			en: "Sim4Sys Designer is a set of add-ons for leading MBSE and system modeling environments—including Eclipse Papyrus, IBM Rhapsody, PTC Modeler, and Dassault Systèmes CATIA Magic—that guides engineers through modeling, automates repetitive tasks, enforces consistency, and can generate requirements, system specifications, and executable software images from the model.",
			fr: "Sim4Sys Designer est un ensemble d’add-ons pour des environnements MBSE et de modélisation système leaders—dont Eclipse Papyrus, IBM Rhapsody, PTC Modeler et Dassault Systèmes CATIA Magic—qui guide les ingénieurs dans la modélisation, automatise les tâches répétitives, renforce la cohérence, et peut générer exigences, spécifications système et images logicielles exécutables à partir du modèle.",
		},
		sections: {
			en: [
				{
					heading: "Why guided modeling matters",
					paragraphs: [
						"Industrial MBSE often fails on process drift: every engineer uses a different subset of the tool, diagrams diverge, and generating trustworthy documents from the model becomes fragile. Sim4Sys Designer embeds methodology and automation inside the modeling environment so teams build higher-quality models faster, with a shorter learning curve.",
						"The goal is not another standalone editor. It is to turn your existing host tool into a guided engineering environment aligned with CIL4Sys’s Customer In the Loop process.",
					],
				},
				{
					heading: "Supported host tools",
					paragraphs: [
						"Sim4Sys Designer is delivered as add-ons for environments already used in industry:",
					],
					bullets: [
						"Eclipse Papyrus (open source)",
						"IBM Rhapsody",
						"PTC Modeler",
						"Dassault Systèmes CATIA Magic",
					],
				},
				{
					heading: "What engineers get day to day",
					paragraphs: [
						"Designer guides the modeling process, automates repetitive steps, and continuously enforces model consistency and engineering best practices. From validated models it can automatically generate requirements, system specifications, and executable software images—creating a path from systems engineering into simulation and validation with Sim4Sys Virtual Bench.",
					],
					bullets: [
						"Methodology guidance inside the modeling UI",
						"Automation of repetitive modeling tasks",
						"Ongoing consistency and best-practice checks",
						"Generation of requirements and specification documents",
						"Generation of executable software images for Virtual Bench",
					],
				},
				{
					heading: "Training ramp",
					paragraphs: [
						"CIL4Sys states that with three days of training followed by three weeks of supervised hands-on practice, engineers can acquire the skills needed to contribute effectively on industrial projects using this approach. See the formation page for the Agile Systems Engineering with Sim4Sys course and related catalog offerings.",
					],
				},
				{
					heading: "Where Designer fits in the Sim4Sys stack",
					paragraphs: [
						"Designer is the modeling front end. Virtual Bench is the collaborative SaaS playground for scenario-based V&V. SyneSys adds an AI companion path from engineering documents into structured models under human-in-the-loop control. Together they support agile systems engineering from model authoring through shared validation.",
						"Published use cases (defense systems of systems, mine warfare, drone vessel inspection) illustrate how model-driven specification and early virtual validation play out on real programs—see the use cases page.",
					],
				},
				{
					heading: "Next steps",
					paragraphs: [
						"Explore the full solutions page for Designer, Virtual Bench, and SyneSys; book or request Sim4Sys training; or contact CIL4Sys to discuss licensing and deployment on your toolchain.",
					],
				},
			],
			fr: [
				{
					heading: "Pourquoi la modélisation guidée compte",
					paragraphs: [
						"Le MBSE industriel échoue souvent par dérive de process : chaque ingénieur utilise un sous-ensemble différent de l’outil, les diagrammes divergent, et générer des documents fiables depuis le modèle devient fragile. Sim4Sys Designer embarque méthodologie et automatisation dans l’environnement de modélisation pour construire des modèles de meilleure qualité plus vite, avec une courbe d’apprentissage plus courte.",
						"L’objectif n’est pas un nouvel éditeur autonome. C’est de transformer votre outil hôte existant en environnement d’ingénierie guidé, aligné sur le process Customer In the Loop de CIL4Sys.",
					],
				},
				{
					heading: "Outils hôtes supportés",
					paragraphs: [
						"Sim4Sys Designer est livré sous forme d’add-ons pour des environnements déjà utilisés en industrie :",
					],
					bullets: [
						"Eclipse Papyrus (open source)",
						"IBM Rhapsody",
						"PTC Modeler",
						"Dassault Systèmes CATIA Magic",
					],
				},
				{
					heading: "Ce que les ingénieurs gagnent au quotidien",
					paragraphs: [
						"Designer guide le processus de modélisation, automatise les étapes répétitives et veille en permanence à la cohérence du modèle et aux bonnes pratiques. À partir de modèles validés, il peut générer automatiquement exigences, spécifications système et images logicielles exécutables—ouvrant un chemin de l’ingénierie système vers la simulation et la validation avec Sim4Sys Virtual Bench.",
					],
					bullets: [
						"Guidage méthodologique dans l’UI de modélisation",
						"Automatisation des tâches répétitives",
						"Contrôles de cohérence et de bonnes pratiques",
						"Génération d’exigences et de documents de spécification",
						"Génération d’images logicielles exécutables pour Virtual Bench",
					],
				},
				{
					heading: "Montée en compétence",
					paragraphs: [
						"CIL4Sys indique qu’avec trois jours de formation suivis de trois semaines de pratique accompagnée, les ingénieurs peuvent acquérir les compétences nécessaires pour contribuer efficacement à des projets industriels avec cette approche. Voir la page formations pour le cours Ingénierie système agile avec Sim4Sys et le catalogue associé.",
					],
				},
				{
					heading: "Place de Designer dans la stack Sim4Sys",
					paragraphs: [
						"Designer est le front de modélisation. Virtual Bench est le playground SaaS collaboratif pour la V&V par scénarios. SyneSys ajoute un compagnon IA pour passer de documents d’ingénierie à des modèles structurés sous contrôle humain. Ensemble, ils soutiennent une ingénierie système agile de l’authoring à la validation partagée.",
						"Les cas d’usage publiés (systèmes de systèmes de défense, guerre des mines, inspection navale par drone) illustrent spécification basée modèles et validation virtuelle précoce—voir la page cas d’usage.",
					],
				},
				{
					heading: "Prochaines étapes",
					paragraphs: [
						"Parcourez la page solutions pour Designer, Virtual Bench et SyneSys ; demandez une formation Sim4Sys ; ou contactez CIL4Sys pour licensing et déploiement sur votre chaîne d’outils.",
					],
				},
			],
		},
		faqs: {
			en: [
				{
					q: "Is Sim4Sys Designer a standalone modeling tool?",
					a: "No. It is a set of add-ons that extend host MBSE tools such as Papyrus, IBM Rhapsody, PTC Modeler, and CATIA Magic with guidance, automation, and generation capabilities.",
				},
				{
					q: "What can Sim4Sys Designer generate from a model?",
					a: "From system models it can automatically generate requirements, system specifications, and executable software images used for simulation and validation workflows with Virtual Bench.",
				},
				{
					q: "How do teams get productive on Sim4Sys Designer?",
					a: "CIL4Sys describes a ramp of three days of training plus three weeks of supervised practice. Details and sessions are listed on the formation page; custom corporate training is available on request.",
				},
			],
			fr: [
				{
					q: "Sim4Sys Designer est-il un outil de modélisation autonome ?",
					a: "Non. C’est un ensemble d’add-ons qui étendent des outils MBSE hôtes (Papyrus, IBM Rhapsody, PTC Modeler, CATIA Magic) avec guidage, automatisation et capacités de génération.",
				},
				{
					q: "Que peut générer Sim4Sys Designer à partir d’un modèle ?",
					a: "À partir des modèles système, il peut générer automatiquement exigences, spécifications système et images logicielles exécutables pour les workflows de simulation et validation avec Virtual Bench.",
				},
				{
					q: "Comment les équipes deviennent-elles productives sur Sim4Sys Designer ?",
					a: "CIL4Sys décrit une montée en compétence de trois jours de formation puis trois semaines de pratique accompagnée. Détails sur la page formations ; formations entreprise sur demande.",
				},
			],
		},
	},
	{
		slug: "functional-digital-mockup",
		datePublished: "2026-10-23",
		dateModified: "2026-10-23",
		title: {
			en: "Functional Digital Mock-up: Validate Before You Build | CIL4Sys",
			fr: "Maquette numérique fonctionnelle : valider avant de construire | CIL4Sys",
		},
		description: {
			en: "A functional digital mock-up lets teams visualize and validate system behavior early using executable models. How Sim4Sys Virtual Bench supports scenario-based V&V before physical integration.",
			fr: "Une maquette numérique fonctionnelle permet de visualiser et valider tôt le comportement système avec des modèles exécutables. Rôle de Sim4Sys Virtual Bench pour la V&V par scénarios avant l’intégration physique.",
		},
		h1: {
			en: "What is a functional digital mock-up?",
			fr: "Qu’est-ce qu’une maquette numérique fonctionnelle ?",
		},
		definition: {
			en: "A functional digital mock-up is a shared, interactive representation of intended system behavior driven by executable models. Unlike a purely geometric mock-up, it lets stakeholders run scenarios, observe dynamics, and check requirements before costly physical development and late integration.",
			fr: "Une maquette numérique fonctionnelle est une représentation partagée et interactive du comportement système visé, pilotée par des modèles exécutables. Contrairement à une maquette purement géométrique, elle permet d’exécuter des scénarios, d’observer la dynamique et de vérifier des exigences avant un développement physique coûteux et une intégration tardive.",
		},
		sections: {
			en: [
				{
					heading: "Geometry vs function",
					paragraphs: [
						"Digital mock-ups in industry often mean 3D geometry: fit, packaging, manufacturing. A functional digital mock-up answers a different question: does the system behave as stakeholders expect under operational scenarios? That includes logic, modes, interfaces, and timing—not only shape.",
						"For software-intensive systems, functional visibility early is where schedule and rework risks concentrate. Waiting for a full integration bench to discover behavioral mismatch is expensive.",
					],
				},
				{
					heading: "What you can do inside the mock-up",
					paragraphs: [
						"With Sim4Sys Virtual Bench, teams run software images inside a functional digital mock-up playground. Scenarios can be built from reusable libraries; parameters of graphical objects connect to model attributes; a controlled simulation clock supports real-time or accelerated runs.",
					],
					bullets: [
						"Trigger events live through the scenario HMI",
						"Schedule events on a timeline or along a trajectory",
						"Trigger events from functional sensors",
						"Define test criteria evaluated while scenarios run",
						"Organize scenarios into test plans and batch execution with reports",
					],
				},
				{
					heading: "Link to Customer In the Loop",
					paragraphs: [
						"The mock-up is the shared stage of Customer In the Loop: stakeholders do not only review documents—they experience behavior. Feedback arrives while models are still easy to change. Agreed behavior then anchors generated requirements and specifications from Sim4Sys Designer.",
					],
				},
				{
					heading: "Before physical integration",
					paragraphs: [
						"Using a functional digital mock-up does not remove the need for HIL benches or physical tests. It shifts discovery left: catch requirement and design issues when the cost of change is lower, then carry validated intent into later verification layers—including model-generated code used as virtual components on HIL benches where applicable.",
					],
				},
				{
					heading: "Next steps",
					paragraphs: [
						"See Virtual Bench on the solutions page, read the Sim4Sys Designer and Customer In the Loop guides, or contact CIL4Sys for a demonstration on your domain scenarios.",
					],
				},
			],
			fr: [
				{
					heading: "Géométrie vs fonction",
					paragraphs: [
						"En industrie, « maquette numérique » signifie souvent la géométrie 3D : encombrement, packaging, fabrication. Une maquette numérique fonctionnelle répond à une autre question : le système se comporte-t-il comme les parties prenantes l’attendent sous des scénarios opérationnels ? Cela inclut logique, modes, interfaces et temporalité—pas seulement la forme.",
						"Pour les systèmes à fort contenu logiciel, la visibilité fonctionnelle précoce concentre les risques planning et reprises. Attendre un banc d’intégration complet pour découvrir un écart de comportement est coûteux.",
					],
				},
				{
					heading: "Ce que l’on fait dans la maquette",
					paragraphs: [
						"Avec Sim4Sys Virtual Bench, les équipes exécutent des images logicielles dans un playground de maquette numérique fonctionnelle. Les scénarios se construisent depuis des bibliothèques réutilisables ; les paramètres d’objets graphiques se lient aux attributs du modèle ; une horloge de simulation contrôlée permet du temps réel ou accéléré.",
					],
					bullets: [
						"Déclencher des événements en live via l’IHM de scénario",
						"Planifier des événements sur une timeline ou une trajectoire",
						"Déclencher des événements via des capteurs fonctionnels",
						"Définir des critères de test évalués pendant l’exécution",
						"Organiser des plans de tests, exécutions batch et rapports",
					],
				},
				{
					heading: "Lien avec Customer In the Loop",
					paragraphs: [
						"La maquette est la scène partagée de Customer In the Loop : les parties prenantes ne relisent pas seulement des documents—elles vivent le comportement. Le feedback arrive tant que les modèles restent faciles à changer. Le comportement convenu ancre ensuite exigences et spécifications générées depuis Sim4Sys Designer.",
					],
				},
				{
					heading: "Avant l’intégration physique",
					paragraphs: [
						"La maquette numérique fonctionnelle ne supprime pas les bancs HIL ni les essais physiques. Elle décale la découverte vers la gauche : attraper les écarts d’exigences et de conception quand le coût du changement est plus bas, puis porter l’intention validée vers les couches de vérification ultérieures—y compris du code issu des modèles comme composants virtuels sur bancs HIL le cas échéant.",
					],
				},
				{
					heading: "Prochaines étapes",
					paragraphs: [
						"Voir Virtual Bench sur la page solutions, lire les guides Sim4Sys Designer et Customer In the Loop, ou contacter CIL4Sys pour une démonstration sur vos scénarios de domaine.",
					],
				},
			],
		},
		faqs: {
			en: [
				{
					q: "How is a functional digital mock-up different from a 3D CAD mock-up?",
					a: "A CAD mock-up focuses on geometry and spatial fit. A functional digital mock-up focuses on executable behavior—modes, logic, interfaces, and scenario outcomes—so stakeholders can validate how the system works, not only how it looks.",
				},
				{
					q: "What tool does CIL4Sys use for the functional digital mock-up?",
					a: "Sim4Sys Virtual Bench is the SaaS playground where software images run in a collaborative functional digital mock-up with scenarios, test criteria, and automated reports.",
				},
				{
					q: "Does early virtual validation replace physical testing?",
					a: "No. It complements later HIL and physical tests by finding behavioral and requirement issues earlier, when fixes are cheaper.",
				},
			],
			fr: [
				{
					q: "En quoi une maquette numérique fonctionnelle diffère-t-elle d’une maquette CAO 3D ?",
					a: "La maquette CAO porte sur la géométrie et l’encombrement. La maquette numérique fonctionnelle porte sur un comportement exécutable—modes, logique, interfaces, résultats de scénarios—pour valider comment le système fonctionne, pas seulement son apparence.",
				},
				{
					q: "Quel outil CIL4Sys utilise-t-il pour la maquette numérique fonctionnelle ?",
					a: "Sim4Sys Virtual Bench est le playground SaaS où des images logicielles s’exécutent dans une maquette numérique fonctionnelle collaborative avec scénarios, critères de test et rapports automatisés.",
				},
				{
					q: "La validation virtuelle précoce remplace-t-elle les essais physiques ?",
					a: "Non. Elle complète HIL et essais physiques en détectant plus tôt les écarts de comportement et d’exigences, quand corriger coûte moins.",
				},
			],
		},
	},
	{
		slug: "synesys-hitl",
		datePublished: "2026-10-23",
		dateModified: "2026-10-23",
		title: {
			en: "SyneSys: AI Companion for Systems Engineering (HITL) | CIL4Sys",
			fr: "SyneSys : compagnon IA pour l’ingénierie système (HITL) | CIL4Sys",
		},
		description: {
			en: "SyneSys is CIL4Sys’s AI companion for systems engineering: ontology-guided, human-in-the-loop conversion of documents into structured models with SysML v2 export. SaaS or on-prem.",
			fr: "SyneSys est le compagnon IA CIL4Sys pour l’ingénierie système : conversion de documents en modèles structurés guidée par ontologies, human-in-the-loop, export SysML v2. SaaS ou on-prem.",
		},
		h1: {
			en: "What is SyneSys?",
			fr: "Qu’est-ce que SyneSys ?",
		},
		definition: {
			en: "SyneSys is CIL4Sys’s AI companion for systems engineering. After configuration to your ontologies, LLMs, and IT constraints, it helps engineers transform engineering documents into structured system models through a step-by-step, human-in-the-loop workflow, with export paths including SysML v2.",
			fr: "SyneSys est le compagnon IA de CIL4Sys pour l’ingénierie système. Après configuration de vos ontologies, LLM et contraintes IT, il aide les ingénieurs à transformer des documents d’ingénierie en modèles système structurés via un workflow pas à pas human-in-the-loop, avec des chemins d’export dont SysML v2.",
		},
		sections: {
			en: [
				{
					heading: "Why human-in-the-loop matters for MBSE AI",
					paragraphs: [
						"Generative AI can draft structure quickly, but systems engineering decisions carry safety, contractual, and integration consequences. SyneSys keeps the engineer in control: each modeling step and its results are reviewed and validated before the AI continues. Productivity from generation pairs with accountable oversight.",
					],
				},
				{
					heading: "Configuration before use",
					paragraphs: [
						"Before deployment, a configuration and validation phase aligns SyneSys with your engineering environment:",
					],
					bullets: [
						"Ontologies reflecting your processes, methods, and domain concepts",
						"Access to the LLMs selected by your organization",
						"Hardware, software, and network setup matching IT and cybersecurity policies",
					],
				},
				{
					heading: "Typical workflow",
					paragraphs: [
						"Users import existing documents, select a system of interest and the appropriate engineering ontology, and ask an AI agent to propose a modeling strategy. SyneSys then progressively builds the system model according to the proposed steps, pausing for human validation.",
						"The resulting model is stored in a tool-independent JSON format. It can be visualized through architecture views, use cases, sequence diagrams, state machines, and activity diagrams, and exported to SysML v2 for integration with the broader MBSE toolchain.",
					],
				},
				{
					heading: "Deployment options",
					paragraphs: [
						"SyneSys is available as a SaaS solution or can be deployed inside your own IT infrastructure. The user interface relies on standard web technologies accessible from a browser. For product details and demos, see https://www.synesys.fr/ and the solutions page on cil4sys.com.",
					],
				},
				{
					heading: "How SyneSys relates to Sim4Sys",
					paragraphs: [
						"SyneSys accelerates the path from documents into structured models. Sim4Sys Designer and Virtual Bench continue the Customer In the Loop journey: guided modeling in industrial host tools and shared functional validation. Together they cover document intake, model quality, and scenario-based V&V.",
					],
				},
				{
					heading: "Next steps",
					paragraphs: [
						"Visit synesys.fr for a demonstration request, review the solutions overview on cil4sys.com, or contact CIL4Sys to discuss on-prem constraints and ontology setup.",
					],
				},
			],
			fr: [
				{
					heading: "Pourquoi le human-in-the-loop compte pour l’IA MBSE",
					paragraphs: [
						"L’IA générative peut proposer une structure rapidement, mais les décisions d’ingénierie système portent des enjeux de sûreté, contractuels et d’intégration. SyneSys garde l’ingénieur aux commandes : chaque étape de modélisation et ses résultats sont revus et validés avant que l’IA poursuive. La productivité de la génération s’allie à une supervision responsable.",
					],
				},
				{
					heading: "Configuration avant usage",
					paragraphs: [
						"Avant déploiement, une phase de configuration et validation aligne SyneSys sur votre environnement d’ingénierie :",
					],
					bullets: [
						"Ontologies reflétant vos processus, méthodes et concepts de domaine",
						"Accès aux LLM choisis par votre organisation",
						"Infrastructure matérielle, logicielle et réseau conforme à vos politiques IT et cybersécurité",
					],
				},
				{
					heading: "Workflow typique",
					paragraphs: [
						"Les utilisateurs importent des documents existants, sélectionnent un système d’intérêt et l’ontologie d’ingénierie adaptée, puis demandent à un agent IA de proposer une stratégie de modélisation. SyneSys construit ensuite progressivement le modèle selon ces étapes, en s’arrêtant pour validation humaine.",
						"Le modèle résultant est stocké dans un format JSON indépendant de l’outil. Il peut être visualisé via des vues d’architecture, cas d’usage, diagrammes de séquence, machines d’états et activités, et exporté vers SysML v2 pour s’intégrer à la chaîne MBSE plus large.",
					],
				},
				{
					heading: "Options de déploiement",
					paragraphs: [
						"SyneSys est disponible en SaaS ou déployable dans votre propre SI. L’interface s’appuie sur des technologies web standard accessibles via navigateur. Pour le détail produit et les démos : https://www.synesys.fr/ et la page solutions sur cil4sys.com.",
					],
				},
				{
					heading: "Lien avec Sim4Sys",
					paragraphs: [
						"SyneSys accélère le passage des documents aux modèles structurés. Sim4Sys Designer et Virtual Bench poursuivent le parcours Customer In the Loop : modélisation guidée dans les outils hôtes industriels et validation fonctionnelle partagée. Ensemble : intake documentaire, qualité de modèle, V&V par scénarios.",
					],
				},
				{
					heading: "Prochaines étapes",
					paragraphs: [
						"Visitez synesys.fr pour une demande de démonstration, parcourez l’overview solutions sur cil4sys.com, ou contactez CIL4Sys pour contraintes on-prem et mise en place d’ontologies.",
					],
				},
			],
		},
		faqs: {
			en: [
				{
					q: "Does SyneSys replace systems engineers?",
					a: "No. It is designed as a human-in-the-loop companion: engineers validate each modeling step. The AI proposes and accelerates; accountability stays with the engineering team.",
				},
				{
					q: "Can SyneSys run on-premises?",
					a: "Yes. CIL4Sys offers SaaS or deployment within your IT infrastructure, with configuration for ontologies, approved LLMs, and cybersecurity constraints.",
				},
				{
					q: "What format does SyneSys produce?",
					a: "Models are stored in tool-independent JSON, viewable as systems engineering diagrams, and can be exported to SysML v2 for broader MBSE toolchains. Product site: https://www.synesys.fr/.",
				},
			],
			fr: [
				{
					q: "SyneSys remplace-t-il les ingénieurs système ?",
					a: "Non. C’est un compagnon human-in-the-loop : les ingénieurs valident chaque étape. L’IA propose et accélère ; la responsabilité reste à l’équipe d’ingénierie.",
				},
				{
					q: "SyneSys peut-il tourner on-premises ?",
					a: "Oui. CIL4Sys propose du SaaS ou un déploiement dans votre SI, avec configuration des ontologies, LLM approuvés et contraintes cybersécurité.",
				},
				{
					q: "Quel format SyneSys produit-il ?",
					a: "Les modèles sont stockés en JSON indépendant de l’outil, visualisables en diagrammes d’ingénierie système, et exportables vers SysML v2. Site produit : https://www.synesys.fr/.",
				},
			],
		},
	},
]

export function getResourceArticle(aSlug: string): ResourceArticle | undefined {
	return RESOURCE_ARTICLES.find((anArticle) => anArticle.slug === aSlug)
}
